import React, { useEffect } from 'react';
import { ErrorBoundary, FallbackProps } from 'react-error-boundary';
import useError from '@/hooks/useError';
import { ERROR_CODE } from '@/constants/errorCode';

interface IErrorBoundaryWrapperProps {
  children: React.ReactNode;
}

declare global {
  interface Window {
    lastError?: Error;
  }
}

// Navigate to error page with direct page reload
const navigateToErrorPage = (errorCode: string) => {
  window.location.href = `/error?code=${errorCode}`;
};

// ErrorBoundary wrapper
export const ErrorBoundaryWrapper = ({
  children,
}: IErrorBoundaryWrapperProps) => {
  const { sendErrorLog } = useError();

  // ErrorFallback
  const ErrorFallback = ({ error }: FallbackProps) => {
    useEffect(() => {
      // Save last error
      if (typeof window !== 'undefined') {
        window.lastError = error;
      }

      // Redirect to error page
      navigateToErrorPage('E_9000');
    }, [error]);

    return <></>;
  };

  const handleError = (
    error: Error,
    info: {
      componentStack?: string | null;
    },
  ) => {
    // Save last error
    if (typeof window !== 'undefined') {
      window.lastError = error;
    }

    sendErrorLog({
      error: `${error}\n'${info.componentStack || 'No component stack available'}`,
      errorCode: ERROR_CODE.E_9000,
      message: 'Component error occurred',
    });
  };

  return (
    <ErrorBoundary FallbackComponent={ErrorFallback} onError={handleError}>
      {children}
    </ErrorBoundary>
  );
};

export const RouterErrorBoundary = ErrorBoundaryWrapper;

export const ErrorPage = ({
  errorCode,
  error,
  message,
}: {
  errorCode: keyof typeof ERROR_CODE;
  error: unknown;
  message?: string;
}) => {
  const { sendErrorLog } = useError();

  useEffect(() => {
    const handleError = async () => {
      await sendErrorLog({
        error,
        errorCode: ERROR_CODE[errorCode],
        message: message ?? 'An error occurred',
      });

      // Redirect to error page
      navigateToErrorPage(errorCode);
    };
    handleError();
  }, [errorCode, error, message, sendErrorLog]);

  return null;
};
